import React, { useState, useEffect } from 'react'
import { Spin, Empty, Input, Select, Space, Typography, Button } from 'antd'
import { SearchOutlined, PlusOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import useSWR from 'swr'
import PersonalContractCard from './PersonalContractCard'
import AddPersonalContractModal from './AddPersonalContractModal'
import { useToken } from '../../hooks'

const { Title } = Typography

const Container = styled.div`
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  min-height: 600px;
`

const Header = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 12px;
`

const StatsRow = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
`

const StatItem = styled.div`
  text-align: center;
  
  .label {
    font-size: 12px;
    color: #999;
    margin-bottom: 4px;
  }
  
  .value {
    font-size: 18px;
    font-weight: 600;
    color: #1890ff;
  }
`

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`

const EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background: white;
  border-radius: 8px;
`

interface PersonalContractListProps {
  userid: string
  fold: boolean
}

const PersonalContractList: React.FC<PersonalContractListProps> = ({ userid, fold }) => {
  const { data: token, level } = useToken()
  const [searchText, setSearchText] = useState('')
  const [exchangeFilter, setExchangeFilter] = useState('all')
  const [filteredData, setFilteredData] = useState<any[]>([])
  const [edit, setEdit] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)

  // API 호출 - token을 가져와서 fetcher 함수 사용
  const { data, error, isLoading, mutate } = useSWR(
    userid && token ? `/admin/exchangepairs?ov=1&userid=${userid}` : null,
    async (url) => {
      const response = await fetch(`${process.env.REACT_APP_API_URL}${url}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      })
      if (!response.ok) {
        throw new Error('Failed to fetch data')
      }
      const result = await response.json()
      return result.result || result
    },
    {
      refreshInterval: 10000, // 10초마다 자동 새로고침
    }
  )

  // 데이터 필터링
  useEffect(() => {
    if (!data || !Array.isArray(data)) return

    let filtered = data.slice()

    // 검색 필터
    if (searchText) {
      filtered = filtered.filter(item =>
        item.pairs?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.groupname?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.code?.toLowerCase().includes(searchText.toLowerCase())
      )
    }

    // 거래소 필터
    if (exchangeFilter !== 'all') {
      filtered = filtered.filter(item => item.exchange_id == exchangeFilter)
    }

    setFilteredData(filtered)
  }, [data, searchText, exchangeFilter])

  // 통계 계산
  const stats = React.useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return { total: 0, personal: 0, exchanges: 0 }

    return {
      total: filteredData.length,
      personal: filteredData.filter(item => item.person).length,
      exchanges: new Set(filteredData.map(item => item.exchange_id)).size
    }
  }, [filteredData])

  // 거래소 목록 추출
  const exchangeOptions = React.useMemo(() => {
    if (!data || !Array.isArray(data)) return []
    
    const exchanges = [...new Set(data.map(item => item.exchange_id))]
    return exchanges.map(id => ({
      value: id,
      label: `거래소 ${id}`
    }))
  }, [data])

  if (isLoading) {
    return (
      <Container>
        <LoadingContainer>
          <Spin size="large" tip="개인별계약 정보를 불러오는 중..." />
        </LoadingContainer>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <EmptyContainer>
          <Empty 
            description="데이터를 불러오는 중 오류가 발생했습니다." 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </EmptyContainer>
      </Container>
    )
  }

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Container>
        <Header>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={5} style={{ margin: 0, color: '#333', fontSize: '16px' }}>
              개인별 계약 관리
            </Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowAddModal(true)}
            >
              개인별계약 추가
            </Button>
          </div>
        </Header>
        
        <EmptyContainer>
          <Empty 
            description={
              <div>
                <p>설정된 개인별계약이 없습니다.</p>
                <p style={{ color: '#999', fontSize: '12px' }}>
                  위의 '개인별계약 추가' 버튼을 클릭하여 새로운 계약을 설정하세요.
                </p>
              </div>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowAddModal(true)}
            >
              첫 번째 개인별계약 추가하기
            </Button>
          </Empty>
        </EmptyContainer>
        
        <AddPersonalContractModal
          visible={showAddModal}
          onCancel={() => setShowAddModal(false)}
          onSuccess={() => mutate()}
          userid={userid}
        />
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={5} style={{ margin: 0, color: '#333', fontSize: '16px' }}>
            개인별 계약 관리
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowAddModal(true)}
          >
            개인별계약 추가
          </Button>
        </div>
        
        <FilterRow>
          <Input
            placeholder="종목명, 그룹명, 코드로 검색..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
          
          <Select
            value={exchangeFilter}
            onChange={setExchangeFilter}
            style={{ width: 150 }}
            options={[
              { value: 'all', label: '전체 거래소' },
              ...exchangeOptions
            ]}
          />
          
          <Space>
            <span style={{ fontSize: '13px', color: '#666' }}>
              총 {filteredData.length}개 항목
            </span>
          </Space>
        </FilterRow>

        <StatsRow>
          <StatItem>
            <div className="label">전체 종목</div>
            <div className="value">{stats.total}</div>
          </StatItem>
          <StatItem>
            <div className="label">개인설정</div>
            <div className="value">{stats.personal}</div>
          </StatItem>
          <StatItem>
            <div className="label">거래소 수</div>
            <div className="value">{stats.exchanges}</div>
          </StatItem>
        </StatsRow>
      </Header>

      {filteredData.length === 0 ? (
        <EmptyContainer>
          <Empty 
            description="검색 조건에 맞는 항목이 없습니다." 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </EmptyContainer>
      ) : (
        <CardGrid>
          {filteredData.map((record, index) => (
            <PersonalContractCard
              key={record.id || index}
              record={record}
              edit={edit}
              setEdit={setEdit}
              mutate={mutate}
              shareMutate={mutate} // 같은 함수 사용
              token={token || ''}
              uid={userid}
              level={level}
            />
          ))}
        </CardGrid>
      )}
      
      <AddPersonalContractModal
        visible={showAddModal}
        onCancel={() => setShowAddModal(false)}
        onSuccess={() => mutate()}
        userid={userid}
      />
    </Container>
  )
}

export default PersonalContractList